/**
 * NurseryLive Delivery Date Checker
 *
 * Features:
 * - Hyperlocal city delivery (1-2 days)
 * - Product-specific city availability
 * - Courier zones for rest of India (3-10 days)
 * - Auto-detect location via OpenStreetMap
 * - Per-item cart delivery dates
 * - Modal-based pincode input
 *
 * Data Sources:
 * - window.DELIVERY_HYPERLOCAL: Shop metafield (delivery.hyperlocal_data)
 * - window.DELIVERY_COURIER: Shop metafield (delivery.courier_zones)
 * - data-product-cities: Product metafield (delivery.hyperlocal_cities)
 */

class DeliveryChecker {
  constructor() {
    this.storageKey = 'nurserylive_pincode';
    this.currentPincode = null;

    // Load shop-level data (handle potential errors from metafield loading)
    this.hyperlocalData = this.parseDeliveryData(window.DELIVERY_HYPERLOCAL, { cities: {}, pincodes: {} });
    this.courierData = this.parseDeliveryData(window.DELIVERY_COURIER, { zones: [], default: { min: 5, max: 7 } });

    // Build lookup maps for O(1) performance
    this.pincodeToCity = new Map(Object.entries(this.hyperlocalData.pincodes || {}));

    // Debug: Log loaded data
    console.log('DeliveryChecker loaded:', {
      hyperlocalCities: Object.keys(this.hyperlocalData.cities || {}),
      pincodeCount: this.pincodeToCity.size,
      courierZones: (this.courierData.zones || []).map(z => z.id),
      hasUnserviceable: !!this.courierData.unserviceable?.enabled
    });

    this.init();
  }

  /**
   * Safely parse delivery data from metafields
   * Handles cases where Shopify returns error objects or strings
   */
  parseDeliveryData(data, fallback) {
    if (!data) return fallback;

    // If it's already an object with expected structure, use it
    if (typeof data === 'object' && !data.error) {
      return data;
    }

    // If it's a string (from multi_line_text_field), try to parse
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.warn('Failed to parse delivery data string:', e);
        return fallback;
      }
    }

    // If it has an error property, return fallback
    if (data.error) {
      console.warn('Delivery data has error:', data.error);
      return fallback;
    }

    return fallback;
  }

  init() {
    this.loadSavedPincode();
    this.bindEvents();
    this.updateAllDisplays();
  }

  // ==========================================
  // MODAL HANDLING
  // ==========================================

  openModal() {
    const modal = document.getElementById('pincode-modal');
    const input = document.getElementById('pincode-input');
    const result = document.getElementById('pincode-result');

    if (modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Reset result display
      if (result) {
        result.style.display = 'none';
        result.innerHTML = '';
      }

      // Focus input after animation
      setTimeout(() => {
        if (input) {
          input.focus();
          input.select();
        }
      }, 100);
    }
  }

  closeModal() {
    const modal = document.getElementById('pincode-modal');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  showModalResult(type, html) {
    const result = document.getElementById('pincode-result');
    if (result) {
      result.className = `pincode-modal__result ${type}`;
      result.innerHTML = html;
      result.style.display = 'block';
    }
  }

  // ==========================================
  // GEOLOCATION - OpenStreetMap
  // ==========================================

  async detectLocation() {
    if (!navigator.geolocation) {
      this.showModalResult('error', '<span class="result-icon">&#x26A0;</span> Geolocation not supported by your browser');
      return null;
    }

    const detectBtn = document.getElementById('detect-location');
    const originalHtml = detectBtn?.innerHTML;

    try {
      if (detectBtn) {
        detectBtn.disabled = true;
        detectBtn.innerHTML = '<svg class="spinner" width="18" height="18" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="40 20" /></svg><span>Detecting...</span>';
      }

      const position = await this.getCurrentPosition();
      const { latitude, longitude } = position.coords;
      const pincode = await this.getPincodeFromCoords(latitude, longitude);

      if (pincode && /^[0-9]{6}$/.test(pincode)) {
        const input = document.getElementById('pincode-input');
        if (input) input.value = pincode;
        this.savePincode(pincode);
        return pincode;
      } else {
        this.showModalResult('error', '<span class="result-icon">&#x26A0;</span> Could not detect pincode. Please enter manually.');
      }
    } catch (error) {
      console.error('Location detection failed:', error);
      const messages = {
        1: 'Location access denied. Please enter pincode manually.',
        2: 'Location unavailable. Please try again.',
        3: 'Location request timed out. Please try again.'
      };
      this.showModalResult('error', `<span class="result-icon">&#x26A0;</span> ${messages[error.code] || 'Could not detect location.'}`);
    } finally {
      if (detectBtn) {
        detectBtn.disabled = false;
        detectBtn.innerHTML = originalHtml;
      }
    }
    return null;
  }

  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      });
    });
  }

  async getPincodeFromCoords(lat, lng) {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
    const response = await fetch(url, {
      headers: { 'User-Agent': 'NurseryLive/1.0' }
    });
    if (!response.ok) throw new Error('Geocoding failed');
    const data = await response.json();
    return data.address?.postcode || null;
  }

  // ==========================================
  // PINCODE VALIDATION & LOOKUP
  // ==========================================

  isValidPincode(pincode) {
    return pincode && /^[0-9]{6}$/.test(pincode);
  }

  getCityFromPincode(pincode) {
    if (!this.isValidPincode(pincode)) return null;
    return this.pincodeToCity.get(pincode) || null;
  }

  getCityDetails(cityId) {
    if (!cityId) return null;
    return this.hyperlocalData.cities?.[cityId] || null;
  }

  getCourierZone(pincode) {
    if (!this.isValidPincode(pincode)) return this.courierData.default;

    // Check if unserviceable
    if (this.courierData.unserviceable?.enabled) {
      const unserviceablePrefixes = this.courierData.unserviceable.prefixes || [];
      for (const prefix of unserviceablePrefixes) {
        if (pincode.startsWith(prefix)) {
          return {
            id: 'unserviceable',
            label: 'Not Serviceable',
            message: this.courierData.unserviceable.message || 'Delivery not available to this pincode',
            isUnserviceable: true
          };
        }
      }
    }

    // Find matching zone by prefix
    for (const zone of (this.courierData.zones || [])) {
      const zonePrefixes = zone.prefixes || [];
      for (const prefix of zonePrefixes) {
        if (pincode.startsWith(prefix)) {
          return zone;
        }
      }
    }

    // Return default zone
    return this.courierData.default || { id: 'default', label: 'Standard', min: 5, max: 7 };
  }

  // ==========================================
  // DELIVERY CALCULATION
  // ==========================================

  getDeliveryForProduct(pincode, productCities = []) {
    if (!this.isValidPincode(pincode)) {
      return { needsPincode: true };
    }

    // Step 1: Check if unserviceable first
    const courierZone = this.getCourierZone(pincode);
    if (courierZone.isUnserviceable) {
      return {
        type: 'unserviceable',
        message: courierZone.message,
        label: courierZone.label
      };
    }

    // Step 2: Check if pincode is in a hyperlocal city
    const cityId = this.getCityFromPincode(pincode);

    // Step 3: If city found, check if product is EXPLICITLY available there
    // Product must have this city in its hyperlocal_cities list to qualify
    // Empty productCities = NOT available for hyperlocal (courier only)
    if (cityId) {
      const isAvailable = productCities.length > 0 && productCities.includes(cityId);

      console.log('Delivery check:', {
        pincode,
        cityId,
        productCities,
        isAvailable
      });

      if (isAvailable) {
        const cityDetails = this.getCityDetails(cityId);
        if (cityDetails) {
          return this.buildDeliveryResult({
            type: 'hyperlocal',
            zone: {
              id: cityId,
              label: cityDetails.label || cityId,
              min: cityDetails.min,
              max: cityDetails.max
            }
          });
        }
      }
    }

    // Step 4: Fall back to courier
    return this.buildDeliveryResult({
      type: 'courier',
      zone: courierZone
    });
  }

  buildDeliveryResult({ type, zone }) {
    const now = Date.now();
    const minDays = parseInt(zone.min, 10) || 1;
    const maxDays = parseInt(zone.max, 10) || 3;

    const minDate = new Date(now + (minDays * 24 * 60 * 60 * 1000));
    const maxDate = new Date(now + (maxDays * 24 * 60 * 60 * 1000));

    return {
      type,
      zone,
      minDate,
      maxDate,
      minDays,
      maxDays,
      formatted: this.formatDateRange(minDate, maxDate),
      formattedShort: this.formatDateShort(minDate, maxDate)
    };
  }

  formatDateRange(minDate, maxDate) {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    const min = minDate.toLocaleDateString('en-IN', options);
    const max = maxDate.toLocaleDateString('en-IN', options);
    return min === max ? min : `${min} - ${max}`;
  }

  formatDateShort(minDate, maxDate) {
    const options = { month: 'short', day: 'numeric' };
    const min = minDate.toLocaleDateString('en-IN', options);
    const max = maxDate.toLocaleDateString('en-IN', options);
    return min === max ? min : `${min} - ${max}`;
  }

  // ==========================================
  // SESSION MANAGEMENT
  // ==========================================

  savePincode(pincode) {
    if (!this.isValidPincode(pincode)) return;

    sessionStorage.setItem(this.storageKey, pincode);
    this.currentPincode = pincode;

    // Show result in modal
    this.showPincodeResult(pincode);

    // Update all displays
    this.updateAllDisplays();

    // Close modal after short delay to show result
    setTimeout(() => this.closeModal(), 1500);

    // Dispatch custom events
    document.dispatchEvent(new CustomEvent('pincode:changed', { detail: { pincode } }));
    window.dispatchEvent(new CustomEvent('pincodeSaved', { detail: { pincode } }));
  }

  showPincodeResult(pincode) {
    const courierZone = this.getCourierZone(pincode);

    if (courierZone.isUnserviceable) {
      this.showModalResult('error', `
        <span class="result-icon">&#x274C;</span>
        <span class="result-title">Delivery not available</span>
        <div>${courierZone.message}</div>
      `);
      return;
    }

    const cityId = this.getCityFromPincode(pincode);

    if (cityId) {
      const city = this.getCityDetails(cityId);
      const delivery = this.buildDeliveryResult({
        type: 'hyperlocal',
        zone: { id: cityId, label: city?.label || cityId, min: city?.min || 1, max: city?.max || 2 }
      });
      this.showModalResult('success', `
        <span class="result-icon">&#x1F680;</span>
        <span class="result-title">Express Delivery to ${city?.label || cityId}</span>
        <div class="result-date">Get it by <strong>${delivery.formatted}</strong></div>
      `);
    } else {
      const delivery = this.buildDeliveryResult({ type: 'courier', zone: courierZone });
      this.showModalResult('success', `
        <span class="result-icon">&#x1F4E6;</span>
        <span class="result-title">Standard Delivery</span>
        <div class="result-date">Get it by <strong>${delivery.formatted}</strong></div>
      `);
    }
  }

  loadSavedPincode() {
    this.currentPincode = sessionStorage.getItem(this.storageKey);
    if (this.currentPincode) {
      const input = document.getElementById('pincode-input');
      if (input) input.value = this.currentPincode;
    }
  }

  // ==========================================
  // UI UPDATES
  // ==========================================

  updateAllDisplays() {
    this.updateHeaderDisplay();
    this.updateProductDisplays();
    this.updateCartDisplay();
  }

  /**
   * Update header pincode display
   */
  updateHeaderDisplay() {
    const selectState = document.getElementById('pincode-select-state');
    const displayState = document.getElementById('pincode-display-state');
    const pincodeValue = document.getElementById('header-pincode-value');

    if (!selectState || !displayState) return;

    if (this.currentPincode) {
      selectState.style.display = 'none';
      displayState.style.display = 'block';
      if (pincodeValue) pincodeValue.textContent = this.currentPincode;
    } else {
      selectState.style.display = 'block';
      displayState.style.display = 'none';
    }
  }

  /**
   * Update all product page delivery displays
   */
  updateProductDisplays() {
    document.querySelectorAll('.delivery-info').forEach(container => {
      this.updateProductDeliveryDisplay(container);
    });
  }

  /**
   * Update individual product delivery display
   */
  updateProductDeliveryDisplay(container) {
    const productId = container.dataset.productId;
    const isAvailable = container.dataset.available !== 'false';

    // Get element references
    const promptEl = document.getElementById(`delivery-prompt-${productId}`);
    const hyperlocalEl = document.getElementById(`delivery-hyperlocal-${productId}`);
    const courierEl = document.getElementById(`delivery-courier-${productId}`);
    const unserviceableEl = document.getElementById(`delivery-unserviceable-${productId}`);

    if (!promptEl) return;

    // Hide all states
    [promptEl, hyperlocalEl, courierEl, unserviceableEl].forEach(el => {
      if (el) el.style.display = 'none';
    });

    // Remove state classes
    container.classList.remove('delivery-info--hyperlocal', 'delivery-info--courier', 'delivery-info--unserviceable');

    // No pincode entered
    if (!this.currentPincode) {
      promptEl.style.display = 'block';
      return;
    }

    // Parse product cities from data attribute
    let productCities = [];
    const citiesData = container.dataset.productCities;

    if (citiesData && citiesData.trim()) {
      try {
        const parsed = JSON.parse(citiesData);
        // Handle both array and object formats
        if (Array.isArray(parsed)) {
          productCities = parsed;
        } else if (typeof parsed === 'object' && parsed !== null) {
          // If it's an object with error, log it
          if (parsed.error) {
            console.warn('Product cities metafield error:', parsed.error);
          }
        }
      } catch (e) {
        // If not JSON, try comma-separated
        productCities = citiesData.split(',').map(s => s.trim()).filter(Boolean);
      }
    }

    console.log(`Product ${productId} cities:`, productCities);

    // Get delivery info
    const delivery = this.getDeliveryForProduct(this.currentPincode, productCities);

    // Update pincode values in all states
    container.querySelectorAll('.js-pincode-value').forEach(el => {
      el.textContent = this.currentPincode;
    });

    // Show appropriate state
    if (delivery.type === 'unserviceable') {
      if (unserviceableEl) {
        unserviceableEl.style.display = 'block';
        const msgEl = unserviceableEl.querySelector('.js-unserviceable-message');
        if (msgEl) msgEl.textContent = delivery.message;
      }
      container.classList.add('delivery-info--unserviceable');
    } else if (delivery.type === 'hyperlocal') {
      if (hyperlocalEl) {
        hyperlocalEl.style.display = 'block';
        const dateEl = hyperlocalEl.querySelector('.js-delivery-date');
        const cityEl = hyperlocalEl.querySelector('.js-city-name');
        if (dateEl) dateEl.textContent = delivery.formatted;
        if (cityEl) cityEl.textContent = delivery.zone.label;
      }
      container.classList.add('delivery-info--hyperlocal');
    } else {
      if (courierEl) {
        courierEl.style.display = 'block';
        const dateEl = courierEl.querySelector('.js-delivery-date');
        if (dateEl) dateEl.textContent = delivery.formatted;
      }
      container.classList.add('delivery-info--courier');
    }
  }

  /**
   * Update cart delivery display
   */
  updateCartDisplay() {
    const cartContainer = document.getElementById('cart-delivery');
    if (!cartContainer) return;

    const promptEl = document.getElementById('cart-delivery-prompt');
    const summaryEl = document.getElementById('cart-delivery-summary');
    const unserviceableEl = document.getElementById('cart-delivery-unserviceable');

    if (!promptEl) return;

    // Hide all states
    [promptEl, summaryEl, unserviceableEl].forEach(el => {
      if (el) el.style.display = 'none';
    });

    // Remove state classes
    cartContainer.classList.remove('cart-delivery--has-pincode', 'cart-delivery--unserviceable');

    // No pincode entered
    if (!this.currentPincode) {
      promptEl.style.display = 'block';
      return;
    }

    // Check if unserviceable at cart level
    const courierZone = this.getCourierZone(this.currentPincode);
    if (courierZone.isUnserviceable) {
      if (unserviceableEl) {
        unserviceableEl.style.display = 'block';
        const msgEl = unserviceableEl.querySelector('.js-unserviceable-message');
        if (msgEl) msgEl.textContent = courierZone.message;
      }
      cartContainer.classList.add('cart-delivery--unserviceable');

      // Update pincode values
      cartContainer.querySelectorAll('.js-pincode-value').forEach(el => {
        el.textContent = this.currentPincode;
      });
      return;
    }

    // Show summary with per-item delivery
    if (summaryEl) {
      summaryEl.style.display = 'block';
      cartContainer.classList.add('cart-delivery--has-pincode');

      // Update pincode value
      summaryEl.querySelectorAll('.js-pincode-value').forEach(el => {
        el.textContent = this.currentPincode;
      });

      // Update each cart item
      document.querySelectorAll('.cart-delivery__item').forEach(item => {
        this.updateCartItemDelivery(item);
      });
    }
  }

  /**
   * Update individual cart item delivery
   */
  updateCartItemDelivery(itemEl) {
    const dateEl = itemEl.querySelector('.js-item-delivery');
    if (!dateEl) return;

    const isOutOfStock = itemEl.dataset.outOfStock === 'true';

    if (isOutOfStock) {
      dateEl.className = 'cart-delivery__item-date js-item-delivery unavailable';
      dateEl.innerHTML = '<span>&#x23F8;</span> Unavailable';
      return;
    }

    // Parse product cities
    let productCities = [];
    const citiesData = itemEl.dataset.productCities;

    if (citiesData && citiesData.trim()) {
      try {
        const parsed = JSON.parse(citiesData);
        if (Array.isArray(parsed)) {
          productCities = parsed;
        }
      } catch (e) {
        productCities = citiesData.split(',').map(s => s.trim()).filter(Boolean);
      }
    }

    const delivery = this.getDeliveryForProduct(this.currentPincode, productCities);

    if (delivery.type === 'hyperlocal') {
      dateEl.className = 'cart-delivery__item-date js-item-delivery hyperlocal';
      dateEl.innerHTML = `<span>&#x1F680;</span> by ${delivery.formattedShort}`;
    } else {
      dateEl.className = 'cart-delivery__item-date js-item-delivery courier';
      dateEl.innerHTML = `<span>&#x1F4E6;</span> by ${delivery.formattedShort}`;
    }
  }

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  bindEvents() {
    // Modal open buttons (header and any js-open-pincode-modal class)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.js-open-pincode-modal, #open-pincode-modal, #edit-pincode-modal')) {
        e.preventDefault();
        this.openModal();
      }
    });

    // Modal close
    const closeBtn = document.getElementById('pincode-modal-close');
    const overlay = document.getElementById('pincode-modal-overlay');

    closeBtn?.addEventListener('click', () => this.closeModal());
    overlay?.addEventListener('click', () => this.closeModal());

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeModal();
    });

    // Submit button
    const submitBtn = document.getElementById('pincode-submit');
    submitBtn?.addEventListener('click', () => this.handleSubmit());

    // Enter key in input
    const input = document.getElementById('pincode-input');
    input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit();
      }
    });

    // Numbers only
    input?.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
    });

    // Detect location button
    const detectBtn = document.getElementById('detect-location');
    detectBtn?.addEventListener('click', () => this.detectLocation());

    // Legacy links (enter-pincode-link, change-pincode-link)
    document.addEventListener('click', (e) => {
      if (e.target.matches('.enter-pincode-link, .change-pincode-link')) {
        e.preventDefault();
        this.openModal();
      }
    });

    // Watch for variant changes (Shopify themes)
    document.addEventListener('variant:changed', () => this.updateAllDisplays());

    // Watch for cart updates
    document.addEventListener('cart:updated', () => this.updateAllDisplays());
  }

  handleSubmit() {
    const input = document.getElementById('pincode-input');
    const pincode = input?.value.trim();

    if (this.isValidPincode(pincode)) {
      this.savePincode(pincode);
    } else {
      this.showModalResult('error', '<span class="result-icon">&#x26A0;</span> Please enter a valid 6-digit pincode');
      input?.focus();
    }
  }
}

// ==========================================
// INITIALIZATION
// ==========================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDeliveryChecker);
} else {
  initDeliveryChecker();
}

function initDeliveryChecker() {
  window.deliveryChecker = new DeliveryChecker();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryChecker;
}
